import { AssignmentTurnedIn as AssignmentTurnedInIcon } from "@mui/icons-material";
import { PromptDialog } from "components/PromptDialog";
import StyledFab from "components/StyledFab";
import { StockStore } from "operations/schema/schema";
import { Dispatch, FC, SetStateAction, memo, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppSelector } from "store";
import {
  selectSelectedJob,
  selectSelectedJobVisit,
  selectVisitCanComplete,
} from "store/slices/visit.store";

interface CompleteVisitButtonProps {
  onCompleteVisit: () => null | undefined;
  setTabValue: Dispatch<SetStateAction<string>>;
}

type Checks = "workTaskChecked" | "requestedPartChecked";

export const CompleteVisitButton: FC<CompleteVisitButtonProps> = memo((props) => {
  const { onCompleteVisit, setTabValue } = props;
  const intl = useIntl();
  const job = useAppSelector(selectSelectedJob);
  const { workTasks, usedParts } = useAppSelector(selectSelectedJobVisit);
  const visitCanComplete = useAppSelector(selectVisitCanComplete);
  const workTaskChecked = "workTaskChecked";
  const requestedPartChecked = "requestedPartChecked";
  const checkedPrompts = useRef<Checks[]>([]);
  const [openIncompleteWorkTaskDialog, setOpenIncompleteWorkTaskDialog] = useState(false);
  const [openUnusedReqPartsDialog, setOpenUnusedReqPartsDialog] = useState(false);
  const [hasUnusedRequestedParts, setHasUnusedRequestedParts] = useState(false);

  useEffect(() => {
    let parts = usedParts.filter((p) => p && p.part?.stockStore !== StockStore.Other);
    var allUsed = job.preOrderedParts.every((pop) =>
      parts.some((up) => up?.part?.id === pop?.id && up?.part?.partNumber === pop?.partNumber)
    );
    setHasUnusedRequestedParts(!allUsed);
  }, [job?.preOrderedParts, usedParts]);

  const check = () => {
    if (
      workTasks.some((wt) => !wt.completedDate) &&
      !checkedPrompts.current.includes(workTaskChecked)
    ) {
      setOpenIncompleteWorkTaskDialog(true);
    } else if (hasUnusedRequestedParts && !checkedPrompts.current.includes(requestedPartChecked)) {
      setOpenUnusedReqPartsDialog(true);
    } else {
      setOpenIncompleteWorkTaskDialog(false);
      setOpenUnusedReqPartsDialog(false);
      onCompleteVisit();
    }
  };

  return (
    <>
      <StyledFab
        className={!visitCanComplete ? "" : "e2e-complete-visit-button"}
        data-testid="CompleteVisitButton"
        disabled={!visitCanComplete}
        onClick={check}
      >
        <AssignmentTurnedInIcon />
      </StyledFab>
      <PromptDialog
        open={openIncompleteWorkTaskDialog}
        setOpen={setOpenIncompleteWorkTaskDialog}
        onOk={() => {
          checkedPrompts.current.push(workTaskChecked);
          check();
        }}
        onCancel={() => {
          setTabValue("tasks"); //WorkTasks
        }}
        promptContent={<FormattedMessage id="visit.signoff.incompleteWorkTasks" />}
      />
      <PromptDialog
        open={openUnusedReqPartsDialog}
        setOpen={setOpenUnusedReqPartsDialog}
        cancelText={intl.formatMessage({ id: "general.continue" })}
        okText={intl.formatMessage({ id: "part.viewParts" })}
        onCancel={() => {
          checkedPrompts.current.push(requestedPartChecked);
          check();
        }}
        onOk={() => {
          setTabValue("parts"); //Parts
        }}
        promptContent={<FormattedMessage id="part.requestedPartsExist" />}
      />
    </>
  );
});
